'use client';

import { useState, useEffect } from 'react';
import { X, Download, Plus, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/utils/api';
import { resolveApkDownloadFromActiveResponse } from '@/utils/app-apk-download';

const DAYS_INTERVAL = 8; // Mostrar a cada 8 dias
const STORAGE_KEY = 'installPwaModalLastShown';

// Detectar se é iOS
const isIOS = () => {
    if (typeof window === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Detectar se é Android
const isAndroid = () => {
    if (typeof window === 'undefined') return false;
    return /Android/.test(navigator.userAgent);
};

// Detectar se já está instalado (PWA)
const isInstalled = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true;
};

export default function InstallPwaModal() {
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const [showModal, setShowModal] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [apkUrl, setApkUrl] = useState<string | null>(null);

    useEffect(() => {
        // Buscar versão do APK
        const fetchApk = async () => {
            try {
                const response = await api.get('app-version/active');
                const url = resolveApkDownloadFromActiveResponse(response.data);
                if (url) setApkUrl(url);
            } catch (error) {
                console.error('Erro ao buscar APK:', error);
            }
        };

        fetchApk();
    }, []);

    useEffect(() => {
        // Não mostrar se não estiver logado ou ainda estiver carregando
        if (authLoading || !isAuthenticated) {
            return;
        }

        // Não mostrar se já estiver instalado
        if (isInstalled()) {
            return;
        }

        // Verificar se deve mostrar o modal
        const lastShown = localStorage.getItem(STORAGE_KEY);

        if (!lastShown) {
            // Primeira vez - mostrar após 1 segundo
            const timer = setTimeout(() => {
                setShowModal(true);
            }, 1000);
            return () => clearTimeout(timer);
        }

        const lastShownDate = new Date(lastShown);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - lastShownDate.getTime()) / (1000 * 60 * 60 * 24));

        // Se passou mais de 8 dias, mostrar novamente
        if (daysDiff >= DAYS_INTERVAL) {
            const timer = setTimeout(() => {
                setShowModal(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [authLoading, isAuthenticated]);

    // Escutar evento beforeinstallprompt (Android)
    useEffect(() => {
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }, []);

    const handleClose = () => {
        setShowModal(false);
        localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    };

    const handleInstall = async () => {
        if (isAndroid() && deferredPrompt) {
            // Android - mostrar prompt de instalação
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;

            if (outcome === 'accepted') {
                console.log('✅ App instalado');
            }

            setDeferredPrompt(null);
            handleClose();
        } else if (isIOS()) {
            // iOS - instruções para adicionar à tela inicial
            handleClose();
        }
    };

    const getTitle = () => {
        if (isIOS()) {
            return 'Adicionar à Tela Inicial';
        } else if (isAndroid()) {
            return 'Baixar Aplicativo';
        }
        return 'Instalar Aplicativo';
    };

    const getDescription = () => {
        if (isIOS()) {
            return 'Toque no botão de compartilhar (□↑) e selecione "Adicionar à Tela Inicial"';
        } else if (isAndroid()) {
            return 'Baixe o APK do aplicativo para uma experiência melhor';
        }
        return 'Instale o aplicativo para uma experiência melhor';
    };

    const handleDownloadApk = () => {
        if (apkUrl) {
            window.open(apkUrl, '_blank');
        } else {
            alert('APK indisponível no momento.');
        }
        handleClose();
    };

    if (showModal && isInstalled()) {
        return null;
    }

    return (
        <AnimatePresence>
            {showModal && (
                <>
                    {/* Overlay */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm"
                        onClick={handleClose}
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className="fixed inset-0 z-[201] flex items-center justify-center p-4 pointer-events-none"
                    >
                        <div
                            className="bg-neutral-950 border border-white/5 rounded-2xl p-6 max-w-sm w-full shadow-2xl pointer-events-auto relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Close button */}
                            <button
                                onClick={handleClose}
                                className="absolute top-4 right-4 text-white/30 hover:text-white/60 transition-colors cursor-pointer"
                            >
                                <X size={18} />
                            </button>

                            {/* Content */}
                            <div className="text-center mb-6">
                                <div className="w-12 h-12 mx-auto mb-4 bg-orange-500/20 rounded-full flex items-center justify-center">
                                    {isIOS() ? (
                                        <Plus size={24} className="text-orange-500" />
                                    ) : (
                                        <Download size={24} className="text-orange-500" />
                                    )}
                                </div>
                                <h2 className="text-xl font-bold text-white mb-2">
                                    {getTitle()}
                                </h2>
                                <p className="text-white/50 text-sm">
                                    {getDescription()}
                                </p>
                            </div>

                            {/* Buttons */}
                            <div className="space-y-3">
                                {isAndroid() ? (
                                    // Android - Mostrar botão de baixar APK
                                    <button
                                        onClick={handleDownloadApk}
                                        className="w-full px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors cursor-pointer flex items-center justify-center gap-2"
                                    >
                                        <Download size={18} />
                                        Baixar APK
                                    </button>
                                ) : isIOS() ? (
                                    // iOS - Mostrar instruções para adicionar à tela inicial
                                    <button
                                        onClick={handleClose}
                                        className="w-full px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors cursor-pointer flex items-center justify-center gap-2"
                                    >
                                        <Check size={18} />
                                        Entendi
                                    </button>
                                ) : (
                                    // Outros navegadores - Mostrar instalação PWA se disponível
                                    deferredPrompt && (
                                        <button
                                            onClick={handleInstall}
                                            className="w-full px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors cursor-pointer"
                                        >
                                            Instalar Agora
                                        </button>
                                    )
                                )}

                                <button
                                    onClick={handleClose}
                                    className="w-full px-6 py-3 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white rounded-lg transition-colors text-sm font-medium cursor-pointer"
                                >
                                    Talvez depois
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

